import React, { useEffect, useRef } from 'react';
import { AppState } from '../../store';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { LocationSearchInput } from '@repo/apg-ui';
import { searchActions, SearchLocation } from '../../store/search';
import { productsActions } from '../../store/products';
import { Loading, UiActions } from '../../store/ui';
import ResultsFilter from '../ResultsFilter';
import './Search.scss';
import { generateCriteria } from '../../utils/payloads';
import { isEqual } from 'lodash';

interface Props {
  searchLocations: SearchLocation[];
  actionsProduct: typeof productsActions;
  actionsSearch: typeof searchActions;
  uiActions: typeof UiActions;
  loading: Loading;
  searchParameters: any;
  basket: any;
  stickyBasketComponent?: any;
  abortControllers: AbortController[];
}

const Search = ({
  searchLocations,
  actionsSearch,
  uiActions,
  actionsProduct,
  basket,
  loading,
  searchParameters,
  stickyBasketComponent,
  abortControllers,
}: Props) => {
  const intl = useIntl();
  const [readySearch, setReadySearch] = React.useState(null);
  const [trackSearchLocations, setTrackSearchLocations] = React.useState(searchLocations);
  const generatePostPayload = (mapPotentialCoordinates?) => {
    const result = {
      sort: [
        {
          column: searchParameters.currentSort,
          direction: searchParameters.sort[searchParameters.currentSort],
        },
      ],
      group: ['format', 'subformat'],
      criteria: generateCriteria(searchLocations, searchParameters),
    };
    if (mapPotentialCoordinates) {
      result['mapview'] = mapPotentialCoordinates;
    }
    return result;
  };

  const currentAbortController = useRef(null);

  useEffect(() => {
    console.log('searchLocations', searchLocations);
    console.log('readySearch', readySearch);
    if ((readySearch === 'add' || readySearch === 'update') && Object.keys(searchLocations).length > 0) {
      setReadySearch(null);
      fetchProducts();
      setTrackSearchLocations(searchLocations);
    } else if (readySearch === 'remove' && !isEqual(searchLocations, trackSearchLocations)) {
      setReadySearch(null);
      fetchProducts();
      setTrackSearchLocations(searchLocations);
    }
  }, [searchLocations, readySearch]);

  const fetchProducts = () => {
    if (loading.productList && abortControllers.length > 0) {
      abortControllers.forEach((controller) => {
        controller.abort();
        uiActions.setLoading('productList', true);
      });
    }
    if (currentAbortController.current) {
      currentAbortController.current.abort();
    }
    currentAbortController.current = new AbortController();
    actionsProduct.addAbortController(currentAbortController.current);
    uiActions.setLoading('productList', true);
    actionsProduct.postSelect(generatePostPayload(), currentAbortController.current.signal).then((res) => {
      if (res === 'Aborting') {
        // That means some other fetch is in progress
        return;
      }
      uiActions.setLoading('productList', false);
      uiActions.setIsShowBottomBar(false);
    });
  };

  return (
    <div className='w-100 d-flex flex-nowrap search-wrap gap-3'>
      <div className='w-100 mt-3 d-flex flex-nowrap search-title'>
        <h3 className='white-space-nowrap'>
          <FormattedMessage id='search.refineResults' defaultMessage='Resultate verfeinern' />
        </h3>
      </div>

      <div className='search-top-section d-flex flex-column gap-3'>
        <div className='d-flex flex-row align-items-end'>
          <LocationSearchInput
            getProduct={(id) => actionsProduct.fetchProductByUrn(id)}
            onRemoved={(address) => {
              setReadySearch('remove');
              actionsSearch.removeLocation(address);
            }}
            onSelected={(address, lngLtd, types, product?, context?, radius?, radiusUnit?) => {
              setReadySearch('add');
              actionsSearch.addLocation({
                address,
                lngLtd,
                types,
                product,
                context,
                radius,
                radiusUnit,
              });
            }}
            onUpdate={(address, radius, radiusUnit) => {
              setReadySearch('update');
              actionsSearch.updateLocation({
                address,
                radius,
                radiusUnit,
              });
            }}
            onHighlightLocation={(location) => {
              console.log('highlighted');
            }}
            updateMapCenter={(centerLatLng) => {}}
            locations={searchLocations}
            restrictedOptionGroups={['trafficcomp', 'postal_code']}
            placeholder={intl.formatMessage({
              id: 'ec.journey01.step01.locationInputPlaceholder',
              defaultMessage: 'Klicken und Suchbegriff eingeben',
            })}
            triggerImportModal={() => {}}
            hideImport={true}
            getSearchResults={(payload) => {
              return actionsSearch.getSearchResults(payload);
            }}
            importButtonLabel={<FormattedMessage id='ec.search.import' defaultMessage="Produkt-ID's importieren" />}
            kilometersLabel={<FormattedMessage id='ec.search.kilometers' defaultMessage='Kilometer' />}
            metersLabel={<FormattedMessage id='ec.search.meters' defaultMessage='Meter' />}
            radiusLabel={<FormattedMessage id='ec.search.radius' defaultMessage='Radius' />}
            translations={{
              places: {
                label: intl.formatMessage({
                  id: 'ec.search.places.label',
                  defaultMessage: 'Orte (Städte, Gemeinden etc.)',
                }),
                description: intl.formatMessage({
                  id: 'ec.search.places.description',
                  defaultMessage: 'Bern, Genf, Zürich...',
                }),
              },
              address: {
                label: intl.formatMessage({
                  id: 'ec.search.address.label',
                  defaultMessage: 'Adressen',
                }),
                description: intl.formatMessage({
                  id: 'ec.search.address.description',
                  defaultMessage: 'Bsp. Giesshübelstrasse 4, 8045 Zürich',
                }),
              },
              cantons: {
                label: intl.formatMessage({
                  id: 'ec.search.cantons.label',
                  defaultMessage: 'Kantone',
                }),
                description: intl.formatMessage({
                  id: 'ec.search.cantons.description',
                  defaultMessage: 'Zürich, Bern, Luzern, Uri, Schwyz, Obwalden...',
                }),
              },
              traffic: {
                label: intl.formatMessage({
                  id: 'ec.search.traffic.label',
                  defaultMessage: 'Verkehrsbetriebe',
                }),
                description: intl.formatMessage({
                  id: 'ec.search.traffic.description',
                  defaultMessage: 'Bernmobil, tl, SBB, ZVV...',
                }),
              },
              postal: {
                label: intl.formatMessage({
                  id: 'ec.search.postal.label',
                  defaultMessage: 'PLZ',
                }),
                description: intl.formatMessage({
                  id: 'ec.search.postal.description',
                  defaultMessage: '8045, 3004, 6003',
                }),
              },
              region: {
                label: intl.formatMessage({
                  id: 'ec.search.regions.label',
                  defaultMessage: 'Regionen',
                }),
                description: intl.formatMessage({
                  id: 'ec.search.regions.description',
                  defaultMessage: 'Zürich, Bern, Lausanne...',
                }),
              },
              id: {
                label: intl.formatMessage({
                  id: 'ec.search.id.label',
                  defaultMessage: 'Produkt-ID',
                }),
                description: intl.formatMessage({
                  id: 'ec.search.id.description',
                  defaultMessage: '213201, 213202. 213203...',
                }),
              },
            }}
          />
          {stickyBasketComponent && <div className='sticky-basket-wrapper'>{stickyBasketComponent}</div>}
        </div>
        <div className='divider-dashed'></div>
        <ResultsFilter loading={loading} basket={basket} />
      </div>
    </div>
  );
};

function mapStateToProps(state: AppState, ownProps: any) {
  return {
    ...ownProps,
    ...state.products,
    ...state.locations,
    ...state.ui,
    searchParameters: state.locations.searchParameters,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actionsProduct: bindActionCreators(productsActions as any, dispatch),
    actionsSearch: bindActionCreators(searchActions as any, dispatch),
    uiActions: bindActionCreators(UiActions as any, dispatch),
    dispatch: dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Search);
